import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useT } from "@/lib/i18n";
import LanguageSwitcher from "./LanguageSwitcher";

const navConfig = [
  { to: "/", labelKey: "nav.home" },
  { to: "/metodo-tao", labelKey: "nav.method" },
  { to: "/mi-historia", labelKey: "nav.story" },
  { to: "/oncologia", labelKey: "nav.oncology" },
  { to: "/fibromialgia", labelKey: "nav.fibromyalgia" },
  { to: "/sesiones-online", labelKey: "nav.online" },
  { to: "/testimonios", labelKey: "nav.testimonials" },
  { to: "/blog", labelKey: "nav.blog" },
  { to: "/prensa", labelKey: "nav.press" },
  { to: "/contacto", labelKey: "nav.contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const t = useT();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-ivory/85 border-b border-sand/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" className="group flex items-center gap-2" data-testid="header-logo">
          <span className="font-serif text-2xl md:text-[28px] tracking-tight text-charcoal italic">
            {t("brand.first")} <span className="text-terracotta not-italic font-medium">TAO</span>
          </span>
          <span className="hidden md:inline text-[11px] uppercase tracking-[0.28em] text-charcoal/60 ml-2">by Marta</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navConfig.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-medium transition-colors ${
                  isActive ? "text-terracotta" : "text-charcoal/75 hover:text-charcoal"
                }`
              }
              end={l.to === "/"}
            >
              {t(l.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to="/sesion-gratuita"
            data-testid="header-cta"
            className="inline-flex items-center gap-2 bg-charcoal text-ivory rounded-full px-5 py-2.5 text-[13px] font-medium hover:bg-terracotta transition-colors"
          >
            {t("nav.freeSession")}
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-charcoal"
          aria-label="Abrir menú · Obrir menú"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sand/60 bg-ivory" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            {navConfig.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 text-base border-b border-sand/40 ${
                    isActive ? "text-terracotta" : "text-charcoal/85"
                  }`
                }
                end={l.to === "/"}
              >
                {t(l.labelKey)}
              </NavLink>
            ))}
            <div className="mt-4 mb-1 flex items-center justify-between">
              <span className="text-[10px] tracking-[0.28em] uppercase text-charcoal/55">{t("lang.switch")}</span>
              <LanguageSwitcher />
            </div>
            <Link
              to="/sesion-gratuita"
              data-testid="mobile-cta"
              className="mt-3 inline-flex justify-center items-center bg-charcoal text-ivory rounded-full px-6 py-3 text-sm font-medium"
            >
              {t("nav.freeSession")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
